import React, { memo, useCallback, useMemo } from 'react';

import { Box, Typography } from '@mui/material';
import { AutocompleteCDS } from '@sharedCR/coreDesignSystem/form';
import PropTypes from 'prop-types';

import {
  CONDITION_REVIEW_LABELS,
  ConditionReviewTestIds,
} from '../../../ConditionReviewList.constants';
import {
  editTaskAssigneeFieldStyles,
  editTaskNameStyles,
  editTaskRowStyles,
} from '../../ConditionReviewItem.styles';

const getBorrowerOptionLabel = option => option.name;
const isBorrowerOptionEqual = (option, value) => option.id === value.id;

// One task row inside the library-condition editor: the (server-resolved) task
// name plus a borrower picker that re-binds its assignee. A memoized component
// with its own stable onChange so the picker isn't handed a new function each
// render (react-perf/jsx-no-new-function-as-prop).
const ConditionTaskAssigneeRow = ({ task, borrowerOptions, assigneeId, onChange }) => {
  const value = useMemo(
    () => borrowerOptions.find(option => option.id === assigneeId) ?? null,
    [borrowerOptions, assigneeId],
  );

  const onAssigneeChange = useCallback(
    (_event, option) => onChange(task.taskTypeId, option),
    [onChange, task.taskTypeId],
  );

  return (
    <Box sx={editTaskRowStyles}>
      <Typography sx={editTaskNameStyles} variant="regular12">
        {task.name ?? CONDITION_REVIEW_LABELS.TASK_FALLBACK}
      </Typography>
      <Box sx={editTaskAssigneeFieldStyles}>
        <AutocompleteCDS
          dataTestId={ConditionReviewTestIds.EDIT_TASK_ASSIGNEE_SELECT}
          getOptionLabel={getBorrowerOptionLabel}
          isOptionEqualToValue={isBorrowerOptionEqual}
          label={CONDITION_REVIEW_LABELS.EDIT_TASK_ASSIGNEE_LABEL}
          name={`condition-task-${task.taskTypeId}-assignee`}
          optionKey="id"
          options={borrowerOptions}
          value={value}
          onChange={onAssigneeChange}
        />
      </Box>
    </Box>
  );
};

ConditionTaskAssigneeRow.propTypes = {
  task: PropTypes.shape({
    taskTypeId: PropTypes.string,
    name: PropTypes.string,
  }).isRequired,
  borrowerOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  assigneeId: PropTypes.string,
};

export default memo(ConditionTaskAssigneeRow);
